import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useAtMentionSearchModes } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useAtMentionSearchModes.js';
import { filterMentionTypeaheadOptions } from '#pplx/frontend/components/form/lexical/plugins/MentionsPluginSupport.js';
import { createPlugin } from '#pplx/frontend/composer-sdk/plugins/createPlugin.js';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
const EMPTY_OMITTED_SEARCH_MODES = [];
function toTypeaheadItem(option) {
    return {
        id: option.uuid,
        label: option.label,
        ...(option.subtitle === undefined ? {} : { subtitle: option.subtitle }),
        ...(option.icon === undefined ? {} : { icon: option.icon }),
        ...(option.disabled === undefined ? {} : { disabled: option.disabled }),
        payload: {
            select: option.action ?? (() => false),
        },
    };
}
function selectSearchMode(item, editor) {
    editor.deleteActiveTrigger();
    // The legacy picker waits for Lexical to commit removal of the slash query before a product
    // selection can rerender the surface or open a gate.
    setTimeout(item.payload.select, 0);
}
function toTypeaheadGroups(options) {
    return options.reduce((groups, option) => {
        if (groups.length === 0 || option.separatorBefore) {
            return [...groups, { id: option.uuid, items: [toTypeaheadItem(option)] }];
        }
        const previous = groups.at(-1);
        return [
            ...groups.slice(0, -1),
            { ...previous, items: [...previous.items, toTypeaheadItem(option)] },
        ];
    }, []);
}
export function useSearchModeTypeaheadPlugin({ omittedSearchModes = EMPTY_OMITTED_SEARCH_MODES, onSearchModeSelected, selectSearchModeOverride, separateComputerMode = false, } = {}) {
    const { $t } = useIntl();
    const openPaywallUpsell = usePaywallUpsell();
    const { mentionSearchModesOptions } = useAtMentionSearchModes({
        enabled: true,
        separateComputerMode,
        omittedSearchModes,
        onSearchModeSelected,
        ...(selectSearchModeOverride && { selectSearchModeOverride }),
        openPaywallUpsell,
    });
    return useMemo(() => {
        return createPlugin({
            id: 'search-mode-typeahead',
            capabilities: [
                {
                    kind: 'typeahead',
                    providers: [
                        {
                            trigger: '/',
                            display: 'inline',
                            section: {
                                id: 'search-mode',
                                title: $t({
                                    defaultMessage: 'Search mode',
                                    id: '8e261c33c3',
                                    description: 'Label in the Mentions Plugin form input section',
                                }),
                            },
                            getResults: (query) => ({
                                groups: toTypeaheadGroups(filterMentionTypeaheadOptions({
                                    options: mentionSearchModesOptions,
                                    queryString: query,
                                })),
                            }),
                            // Registration erases this provider's item subtype, but every selected item was
                            // created by this provider's result mapper.
                            onSelect: (item, editor) => selectSearchMode(item, editor),
                        },
                    ],
                },
            ],
        });
    }, [$t, mentionSearchModesOptions]);
}
//# sourceMappingURL=useSearchModeTypeaheadPlugin.js.map